import { esc, idSource, n, safeHex, shade } from '../drawing/svg';
import { SHAPES, type StartBrief } from './startBrief';

/**
 * A rough floor plan of the room the shopper describes, from the mockup's
 * `planner.html` (`runsFor`, `modules`, `planSVG`). It is a sketch to show the
 * brief was understood, not a layout: the planner does the real one. So it
 * places 600 mm modules along each run and marks the first appliances, and
 * prices nothing.
 */
interface Run {
  x: number;
  y: number;
  len: number;
  dir: 'h' | 'v';
  island?: boolean;
}

const DEPTH = 600;

function runsFor(b: StartBrief): Run[] {
  const [a = 3600, bb = 2800, c = 2600] = b.walls;
  switch (b.shape) {
    case 'single':
      return [{ x: 0, y: 0, len: a, dir: 'h' }];
    case 'galley':
      return [
        { x: 0, y: 0, len: a, dir: 'h' },
        { x: 0, y: 2400, len: bb, dir: 'h' },
      ];
    case 'l':
      return [
        { x: 0, y: 0, len: a, dir: 'h' },
        { x: 0, y: DEPTH, len: bb - DEPTH, dir: 'v' },
      ];
    case 'u':
      return [
        { x: 0, y: 0, len: bb, dir: 'h' },
        { x: 0, y: DEPTH, len: a - DEPTH, dir: 'v' },
        { x: bb - DEPTH, y: DEPTH, len: c - DEPTH, dir: 'v' },
      ];
    case 'island':
      return [
        { x: 0, y: 0, len: a, dir: 'h' },
        { x: 0, y: DEPTH, len: bb - DEPTH, dir: 'v' },
        { island: true, x: a * 0.35, y: DEPTH + 1100, len: Math.min(2400, a * 0.45), dir: 'h' },
      ];
  }
}

function modules(len: number): number[] {
  const count = Math.max(0, Math.floor(len / 600));
  const rest = len - count * 600;
  const out = Array<number>(count).fill(600);
  if (rest >= 150) out.push(rest);
  return out;
}

const APPLIANCES = ['Sink', 'Hob', 'Oven', 'Dishwasher', 'Fridge freezer'];

export function roomPlanSvg(b: StartBrief, colour: string, idPrefix = 'plan'): string {
  const hex = safeHex(colour);
  const grid = idSource(idPrefix).next();
  const runs = runsFor(b);
  const W = Math.max(...runs.map((r) => r.x + (r.dir === 'h' ? r.len : DEPTH))) + 200;
  const H = Math.max(...runs.map((r) => r.y + (r.dir === 'v' ? r.len : DEPTH)), 2600) + 400;
  const names = SHAPES[b.shape].walls;
  const tags = b.must.filter((m) => APPLIANCES.includes(m));
  let g = `<svg viewBox="-300 -300 ${n(W + 400)} ${n(H + 200)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(`Sketch of a ${SHAPES[b.shape].name.toLowerCase()} kitchen`)}">`;
  g += `<defs><pattern id="${grid}" width="200" height="200" patternUnits="userSpaceOnUse"><path d="M200 0H0V200" fill="none" stroke="#ece5da" stroke-width="4"/></pattern></defs>`;
  g += `<rect x="-300" y="-300" width="${n(W + 400)}" height="${n(H + 200)}" fill="url(#${grid})"/>`;
  g += `<path d="M-60 ${n(H - 250)} V-60 H${n(W - 140)}" stroke="#1d211f" stroke-width="60" fill="none"/>`;
  const labels: string[] = [];
  runs.forEach((r, ri) => {
    let off = 0;
    modules(r.len).forEach((m, mi) => {
      const x = r.dir === 'h' ? r.x + off : r.x;
      const y = r.dir === 'h' ? r.y : r.y + off;
      const w = r.dir === 'h' ? m : DEPTH;
      const h = r.dir === 'h' ? DEPTH : m;
      const tag = !r.island && mi > 0 ? tags.shift() : undefined;
      g += `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" fill="${tag ? '#efe4d2' : hex}" stroke="${shade(hex, -0.3)}" stroke-width="6"/>`;
      if (tag) {
        const cx = x + w / 2;
        const cy = y + h / 2;
        g +=
          tag === 'Hob'
            ? `<g fill="none" stroke="#a8834a" stroke-width="12">${[[-120, -120], [120, -120], [-120, 120], [120, 120]].map(([dx, dy]) => `<circle cx="${n(cx + (dx as number))}" cy="${n(cy + (dy as number))}" r="80"/>`).join('')}</g>`
            : tag === 'Sink'
              ? `<rect x="${n(cx - 200)}" y="${n(cy - 160)}" width="400" height="320" rx="40" fill="#fff" stroke="#a8834a" stroke-width="12"/>`
              : `<rect x="${n(cx - 220)}" y="${n(cy - 220)}" width="440" height="440" rx="20" fill="none" stroke="#a8834a" stroke-width="12"/>`;
        labels.push(
          r.dir === 'v'
            ? `<text x="${n(x + 700)}" y="${n(cy + 30)}" font-family="Manrope,sans-serif" font-size="84" font-weight="700" fill="#7a5e2f">${esc(tag)}</text>`
            : `<text x="${n(cx)}" y="${n(cy + 420)}" text-anchor="middle" font-family="Manrope,sans-serif" font-size="84" font-weight="700" fill="#7a5e2f">${esc(tag)}</text>`,
        );
      }
      off += m;
    });
    const wx = r.x - 20;
    const wy = r.y - 20;
    g += `<rect x="${n(wx)}" y="${n(wy)}" width="${n((r.dir === 'h' ? r.len : DEPTH) + 40)}" height="${n((r.dir === 'v' ? r.len : DEPTH) + 40)}" fill="none" stroke="#c49a6c" stroke-width="22" opacity=".9"/>`;
    if (r.island) {
      g += `<text x="${n(r.x + r.len / 2)}" y="${n(r.y + 900)}" text-anchor="middle" font-family="Manrope,sans-serif" font-weight="800" font-size="100" fill="#4a504c">Island</text>`;
    } else {
      const label = `Wall ${names[ri] ?? ''} · ${b.walls[ri] ?? r.len}mm`;
      g +=
        r.dir === 'h'
          ? `<text x="${n(r.x + r.len / 2)}" y="-140" text-anchor="middle" font-family="Manrope,sans-serif" font-weight="800" font-size="110" fill="#4a504c">${esc(label)}</text>`
          : `<text x="-150" y="${n(r.y + r.len / 2)}" text-anchor="middle" font-family="Manrope,sans-serif" font-weight="800" font-size="110" fill="#4a504c" transform="rotate(-90 -150 ${n(r.y + r.len / 2)})">${esc(label)}</text>`;
    }
  });
  return `${g}${labels.join('')}</svg>`;
}

/** The small shape icon on each room-shape button. */
export function shapeIconSvg(shape: StartBrief['shape']): string {
  const d = {
    single: 'M8 10h40v8H8z',
    galley: 'M8 8h40v7H8zM8 41h40v7H8z',
    l: 'M8 8h40v8H8zM8 16h8v32H8z',
    u: 'M8 8h40v8H8zM8 16h8v32H8zM40 16h8v32h-8z',
    island: 'M8 8h40v8H8zM8 16h8v32H8zM24 30h18v10H24z',
  }[shape];
  return `<svg viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="4" width="48" height="48" rx="3" fill="#fff" stroke="#d3c7b6"/><path d="${d}" fill="#8a9a82"/></svg>`;
}
